'use client'

import * as React from 'react'
import { Handshake, Mail, DollarSign, FolderKanban } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { Progress } from 'components/ui/progress'
import { cn } from 'lib/utils'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

const engagementScore = {
  inform: 20,
  consult: 40,
  involve: 60,
  collaborate: 80,
  empower: 100,
}

const influenceColors = {
  high: 'bg-destructive/15 text-destructive',
  medium: 'bg-warning/15 text-warning',
  low: 'bg-success/15 text-success',
}

export default function PartnersPage() {
  const [partners, setPartners] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [partnerLinks, setPartnerLinks] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadPartners() {
      try {
        const [stakeholderSnap, projectSnap, partnerSnap] = await Promise.all([
          getDocs(collection(db, 'stakeholders')),
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'partners')),
        ])
        if (!isMounted) return
        const stakeholders = stakeholderSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const filteredPartners = stakeholders.filter(
          (stakeholder) => String(stakeholder.type || '').toLowerCase() === 'partner',
        )
        setPartners(filteredPartners)
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setPartnerLinks(partnerSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setPartners([])
        setProjects([])
        setPartnerLinks([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadPartners()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const normalizeKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

  const partnerProjectMap = partnerLinks.reduce((acc, link) => {
    const org = normalizeKey(link.orgName)
    if (!org) return acc
    if (!acc[org]) acc[org] = new Set()
    if (link.projectId) acc[org].add(link.projectId)
    return acc
  }, {})

  const partnerCards = partners.map((partner) => {
    const directProjectIds = Array.isArray(partner.projectIds) ? partner.projectIds : []
    const orgKey = normalizeKey(partner.organization || partner.name)
    const linkedFromPartners = partnerProjectMap[orgKey] ? Array.from(partnerProjectMap[orgKey]) : []
    const mergedProjectIds = Array.from(new Set([...directProjectIds, ...linkedFromPartners]))
    const linkedProjects = mergedProjectIds.map((id) => projectMap[id]).filter(Boolean)
    const influence = partner.influence || 'medium'
    const interest = partner.interest || 'medium'
    const engagement = partner.engagementLevel || 'consult'
    const engagementValue = engagementScore[engagement] || 40
    return {
      ...partner,
      projectCount: linkedProjects.length,
      linkedProjects,
      influence,
      interest,
      engagement,
      engagementValue,
    }
  })

  const partnerChartData = partnerCards.map((partner) => ({
    partner: (partner.organization || partner.name || 'Partner').slice(0, 10) + (partner.organization?.length > 10 ? '...' : ''),
    projects: partner.projectCount,
    engagement: Math.round(partner.engagementValue / 10),
  }))

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Development Partners</h1>
          <p className="text-sm text-muted-foreground mt-1">Partner relationships and engagement across the portfolio</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs text-foreground gap-1">
            <Handshake className="size-3" />
            {loading ? '—' : partnerCards.length} Active Partners
          </Badge>
        </div>
      </div>

      {/* Summary Chart */}
      <Card className="border border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Partner Portfolio Overview</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={partnerChartData} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
              <XAxis dataKey="partner" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
              <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                formatter={(v, name) => [v, name]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="projects" fill="oklch(0.35 0.12 145)" name="Projects" radius={[4, 4, 0, 0]} />
              <Bar dataKey="engagement" fill="oklch(0.87 0.15 85)" name="Engagement Score" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Partner Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {partnerCards.map((partner) => {
          return (
            <Card key={partner.id} className="border border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10"><Handshake className="size-5 text-primary" /></div>
                    <div>
                      <CardTitle className="text-sm font-semibold text-foreground">{partner.organization || partner.name || 'Partner'}</CardTitle>
                      <CardDescription className="text-xs">
                        {partner.projectCount} project{partner.projectCount !== 1 ? 's' : ''} | {partner.role || 'Partner'}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={cn('text-[10px]', influenceColors[partner.influence])}>
                    {partner.influence} influence
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {/* Budget */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-muted/50 p-2.5 text-center">
                    <DollarSign className="size-4 text-primary mx-auto" />
                    <p className="text-sm font-bold text-foreground mt-1">{partner.projectCount}</p>
                    <p className="text-[10px] text-muted-foreground">Projects</p>
                  </div>
                  <div className="rounded-lg bg-success/5 p-2.5 text-center">
                    <FolderKanban className="size-4 text-success mx-auto" />
                    <p className="text-sm font-bold text-success mt-1">{partner.interest}</p>
                    <p className="text-[10px] text-muted-foreground">Interest</p>
                  </div>
                  <div className="rounded-lg bg-destructive/5 p-2.5 text-center">
                    <p className="text-sm font-bold text-destructive mt-1">{partner.engagement}</p>
                    <p className="text-[10px] text-muted-foreground">Engagement</p>
                  </div>
                </div>

                {/* Compliance Bar */}
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between">
                    <span className="text-[10px] text-muted-foreground">Engagement Strength</span>
                    <span className="text-[10px] font-semibold text-foreground">{partner.engagementValue}%</span>
                  </div>
                  <Progress value={partner.engagementValue} className="h-2" />
                </div>

                {/* Awards */}
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Linked Projects</p>
                  {partner.linkedProjects.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No linked projects</p>
                  ) : (
                    partner.linkedProjects.map((project) => (
                      <div key={project.id} className="flex items-center justify-between rounded bg-muted/30 px-2.5 py-1.5">
                        <div>
                          <span className="text-xs font-medium text-foreground">{project.code || project.shortCode || 'Project'}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">
                            {project.title?.length > 35 ? `${project.title.slice(0, 35)}...` : project.title}
                          </span>
                        </div>
                        <Badge variant="outline" className={cn('text-[10px]', project.status === 'active' ? 'text-success' : project.status === 'planning' ? 'text-warning' : 'text-muted-foreground')}>
                          {project.status || 'planning'}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>

                {/* Contacts */}
                {(partner.email || partner.phone) && (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Key Contacts</p>
                    <div className="flex items-center gap-2">
                      <Avatar className="size-6">
                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                          {(partner.name || 'P').split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <span className="text-xs font-medium text-foreground">{partner.name || 'Partner'}</span>
                        {partner.role && <span className="text-[10px] text-muted-foreground ml-2">{partner.role}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      {partner.email && <span className="flex items-center gap-1"><Mail className="size-3" />{partner.email}</span>}
                      {partner.phone && <span className="flex items-center gap-1">{partner.phone}</span>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
